declare global {
  interface Window {
    posthog?: { capture: (eventName: string, properties?: Record<string, unknown>) => void };
    posthogSafeCapture?: (eventName: string, properties?: Record<string, unknown>) => boolean;
    __tallyEmbedPhListener?: boolean;
  }
}

function isTallyOrigin(origin: string): boolean {
  try {
    const host = new URL(origin).hostname;
    return host === "tally.so" || host.endsWith(".tally.so");
  } catch {
    return false;
  }
}

function captureTallyEvent(eventName: string, properties: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  const base = {
    ...properties,
    page_path: window.location.pathname,
    page_title: typeof document !== "undefined" ? document.title : "",
    timestamp: new Date().toISOString(),
  };
  if (window.posthogSafeCapture?.(eventName, base)) return;
  if (window.posthog && typeof window.posthog.capture === "function") {
    try {
      window.posthog.capture(eventName, base);
    } catch {
      /* silenced */
    }
  }
}

const formLoadedAtMs = new Map<string, number>();

/**
 * Escucha única: eventos Tally por postMessage (embed con `formEventsForwarding=1`).
 * No envía respuestas ni PII a PostHog — solo ids, conteos, tipos de campo y tiempos.
 * Llamar una vez al arranque (p. ej. desde `main.tsx` tras `posthogSafeCapture`).
 */
export function initTallyEmbedPostMessageTracking(): void {
  if (typeof window === "undefined") return;
  if (window.__tallyEmbedPhListener) return;
  window.__tallyEmbedPhListener = true;

  window.addEventListener("message", (e) => {
    if (typeof e.data !== "string") return;
    if (!isTallyOrigin(String(e.origin))) return;
    if (!e.data.includes("Tally.")) return;

    let envelope: { payload?: Record<string, unknown> };
    try {
      envelope = JSON.parse(e.data) as { payload?: Record<string, unknown> };
    } catch {
      return;
    }
    const payload = envelope.payload;
    if (!payload || typeof payload !== "object") return;

    const formId = typeof payload.formId === "string" ? payload.formId : "";

    if (e.data.includes("Tally.FormLoaded")) {
      const key = formId || "_";
      formLoadedAtMs.set(key, Date.now());
      captureTallyEvent("tally_embed_form_loaded", {
        tally_event: "form_loaded",
        tally_form_id: formId || null,
      });
      return;
    }

    if (e.data.includes("Tally.FormPageView")) {
      captureTallyEvent("tally_embed_form_page_view", {
        tally_event: "form_page_view",
        tally_form_id: formId || null,
        tally_form_page:
          typeof payload.page === "number" && Number.isFinite(payload.page) ? payload.page : null,
      });
      return;
    }

    if (e.data.includes("Tally.FormSubmitted")) {
      const key = formId || "_";
      const started = formLoadedAtMs.get(key);
      formLoadedAtMs.delete(key);
      const msToSubmit = typeof started === "number" ? Date.now() - started : null;

      const fields = Array.isArray(payload.fields) ? payload.fields : [];
      const fieldTypes = fields
        .map((f) => (f && typeof f === "object" && "type" in f ? String((f as { type?: string }).type) : ""))
        .filter(Boolean);

      const meta = {
        tally_event: "form_submitted",
        tally_form_id: formId || null,
        tally_form_name: typeof payload.formName === "string" ? payload.formName : null,
        tally_submission_id: typeof payload.id === "string" ? payload.id : null,
        tally_respondent_id: typeof payload.respondentId === "string" ? payload.respondentId : null,
        tally_fields_count: fields.length,
        tally_field_types: fieldTypes.slice(0, 32),
        tally_ms_from_loaded_to_submit: msToSubmit,
      };

      captureTallyEvent("tally_embed_form_submitted", meta);
      captureTallyEvent("conversion_funnel_step", {
        funnel_step: "tally_form_submitted",
        tally_form_id: formId || null,
        tally_ms_from_loaded_to_submit: msToSubmit,
        tally_fields_count: fields.length,
      });
      return;
    }

    if (e.data.includes("Tally.PopupClosed")) {
      captureTallyEvent("tally_embed_popup_closed", {
        tally_event: "popup_closed",
        tally_form_id: formId || null,
      });
    }
  });
}
